// Telnyx WebRTC lane of the agent dialer.
// Telnyx has no TwiML-style webhook for outbound, so the caller ID travels with
// newCall() and the call log is written from here.

import { useState, useEffect, useRef, useCallback } from "react";
import { TelnyxRTC } from "@telnyx/webrtc";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const TELNYX_RTC_ENV = "https://rtc.telnyx.com";

// Telnyx call.state values that mean "the other side picked up".
const ACTIVE_STATES = ["active"];
const RINGING_STATES = ["trying", "requesting", "recovering", "ringing", "early"];
const ENDED_STATES = ["hangup", "destroy", "purge"];

// Unanswered ringing timeout — agar samne wala 30 second tak call na uthaye to
// dialer khud hi hang up kar de, taaki carrier-side failover aur infinite redial loop na bane.
const RING_TIMEOUT_MS = 30000;

const authToken = () => localStorage.getItem("token") || "";

// Telnyx attaches the remote stream to a media element — without one the callee
// is simply inaudible. Kept off-screen and reused for every call.
const REMOTE_AUDIO_ID = "rxpt-dialer-remote-audio";

const ensureRemoteAudio = () => {
  let el = document.getElementById(REMOTE_AUDIO_ID);
  if (!el) {
    el = document.createElement("audio");
    el.id = REMOTE_AUDIO_ID;
    el.autoplay = true;
    el.setAttribute("playsinline", "true");
    el.style.display = "none";
    document.body.appendChild(el);
  }
  return el;
};

/*
 * Hold par customer ko kya sunai de.
 *
 * SDK ka call.hold() SIP hold hai — dono taraf media ruk jaati hai aur customer
 * ko sirf sannata milta hai. Telnyx ka hold music sirf Call Control legs par
 * chalta hai, aur browser wali call credential connection par hoti hai, isliye
 * wo raasta hai hi nahi. To yahan mic track ki jagah ye announcement bhej dete
 * hain — media chalti rehti hai aur customer ko pata chalta hai ki hold par hai.
 *
 * Filename ka case bilkul waisa hi rakhna — Linux par URL case-sensitive hai.
 */
const HOLD_AUDIO_URL = "/sounds/Hold-sound.mp3";
const HOLD_GAP_MS = 2000;

const createHoldSource = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;

  try {
    const ctx = new Ctx();
    const dest = ctx.createMediaStreamDestination();
    // Gesture ke baad bhi Safari me context suspended reh sakta hai = chuppi.
    ctx.resume().catch(() => {});

    const el = new Audio(HOLD_AUDIO_URL);
    // createMediaElementSource sirf graph me bhejta hai, agent ke speaker par nahi.
    ctx.createMediaElementSource(el).connect(dest);

    // Line poori hone ke baad 2 second gap, phir dobara.
    let timer = null;
    const play = () => el.play().catch(() => {});
    el.addEventListener("ended", () => {
      timer = setTimeout(play, HOLD_GAP_MS);
    });
    play();

    const stop = () => {
      clearTimeout(timer);
      el.pause();
      try {
        ctx.close();
      } catch (_) {}
    };

    return { track: dest.stream.getAudioTracks()[0], stop };
  } catch (_) {
    return null;
  }
};

// Jis sender par hamari awaaz jaati hai — hold par isi ka track badalte hain.
const audioSender = (call) => {
  try {
    return (call?.peer?.instance?.getSenders() || []).find((s) => s.track?.kind === "audio") || null;
  } catch (_) {
    return null;
  }
};

const apiPost = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken()}`,
    },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.message || "Request failed");
    err.status = res.status;
    throw err;
  }
  return data;
};

export function useTelnyxDialer({ enabled, agentId, callerId, callerName, initialAuth }) {
  const [deviceStatus, setDeviceStatus] = useState("idle");
  const [callState, setCallState] = useState("idle");
  const [callDuration, setCallDuration] = useState(0);
  const [incomingFrom, setIncomingFrom] = useState("");
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [callEndReason, setCallEndReason] = useState(null);

  const clientRef = useRef(null);
  const activeCallRef = useRef(null);
  const timerRef = useRef(null);
  const ringTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const localHangupRef = useRef(false);
  const answeredRef = useRef(false);
  // Jis call ka end handle ho chuka — dubara na chale.
  const endedCallIdRef = useRef(null);
  const logIdRef = useRef(null);
  const stampedIdRef = useRef(false);
  const durationRef = useRef(0);
  // Hold ke dauraan: bajta hua tone, aur mic ka asli track jo wapas lagana hai.
  const holdSourceRef = useRef(null);
  const micTrackRef = useRef(null);

  const clearRingTimer = useCallback(() => {
    if (ringTimerRef.current) {
      clearTimeout(ringTimerRef.current);
      ringTimerRef.current = null;
    }
  }, []);

  const safeSet = useCallback(
    (setter) => (...args) => {
      if (isMountedRef.current) setter(...args);
    },
    []
  );

  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    durationRef.current = 0;
    safeSet(setCallDuration)(0);
    timerRef.current = setInterval(() => {
      durationRef.current += 1;
      if (isMountedRef.current) setCallDuration(durationRef.current);
    }, 1000);
  }, [safeSet]);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  // Tone band, mic wapas, aur agent ko customer dobara sunai dene lage.
  const releaseHold = useCallback((call) => {
    const source = holdSourceRef.current;
    const mic = micTrackRef.current;
    holdSourceRef.current = null;
    micTrackRef.current = null;

    const el = document.getElementById(REMOTE_AUDIO_ID);
    if (el) el.muted = false;

    if (!source) return false;

    const sender = audioSender(call);
    if (sender && mic) sender.replaceTrack(mic).catch(() => {});
    source.stop();
    return true;
  }, []);

  const patchCallLog = useCallback(async (id, body) => {
    if (!id) return;
    try {
      await fetch(`${API_BASE}/dialer/calls/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken()}`,
        },
        body: JSON.stringify(body),
      });
    } catch (_) {}
  }, []);

  // Close the log row we opened when the call started.
  const closeCallLog = useCallback(
    async (failed = false) => {
      const id = logIdRef.current;
      logIdRef.current = null;
      await patchCallLog(id, {
        answered: answeredRef.current,
        durationSeconds: durationRef.current,
        failed,
      });
    },
    [patchCallLog]
  );

  const resetCallState = useCallback(
    (reason = "local") => {
      stopTimer();
      clearRingTimer();
      // Hold me hi call kat gayi to tone bajti reh jaati aur mic muted rehta.
      releaseHold(activeCallRef.current);
      safeSet(setCallState)("idle");
      safeSet(setCallDuration)(0);
      safeSet(setMuted)(false);
      safeSet(setHeld)(false);
      safeSet(setIncomingFrom)("");
      safeSet(setCallEndReason)(reason);

      activeCallRef.current = null;
      localHangupRef.current = false;
      answeredRef.current = false;
      stampedIdRef.current = false;

      setTimeout(() => {
        if (isMountedRef.current) setCallEndReason(null);
      }, 4000);
    },
    [stopTimer, clearRingTimer, safeSet, releaseHold]
  );

  const handleCallUpdate = useCallback(
    (call) => {
      const state = call?.state;
      if (!state) return;

      if (call.options?.remoteCallerNumber && call.direction === "inbound") {
        safeSet(setIncomingFrom)(call.options.remoteCallerNumber);
      }

      // The carrier's call id only appears once the leg is up — stamp it then so
      // Telnyx webhooks can find this row.
      if (logIdRef.current && !stampedIdRef.current) {
        let controlId = null;
        try {
          controlId = call.telnyxIDs?.telnyxCallControlId || null;
        } catch (_) {}
        if (controlId) {
          stampedIdRef.current = true;
          patchCallLog(logIdRef.current, { callControlId: controlId, ended: false });
        }
      }

      if (RINGING_STATES.includes(state)) {
        // Loop guard: Agar call pehle answer/media tak pohoch chuki thi (jaise carrier announcement
        // "user is not answering") aur ab Telnyx failover karke wapas trying/ringing bhej raha hai,
        // to ye auto-redial loop hai — is call ko turant kaat do.
        if (answeredRef.current) {
          console.warn("[Telnyx] Call was answered/in-progress but re-entered ringing (failover redial loop) — hanging up");
          clearRingTimer();
          localHangupRef.current = true;
          try {
            call.hangup();
          } catch (_) {}
          closeCallLog(false);
          resetCallState("no-answer");
          return;
        }

        activeCallRef.current = call;
        safeSet(setCallState)(call.direction === "inbound" ? "incoming" : "ringing");
        return;
      }

      if (ACTIVE_STATES.includes(state)) {
        clearRingTimer();
        activeCallRef.current = call;
        answeredRef.current = true;
        safeSet(setCallState)("active");
        startTimer();
        return;
      }

      if (ENDED_STATES.includes(state)) {
        clearRingTimer();
        // SDK hangup ke turant baad destroy bhi bhejta hai, aur tab tak neeche wala
        // reset refs saaf kar chuka hota hai — dubara chala to reason "no-answer" ban jaata.
        if (endedCallIdRef.current === call.id) return;
        endedCallIdRef.current = call.id;

        /*
         * Telnyx jab call reject karta hai to wo WebSocket pe hangup bhejta hai
         * aur wajah call object pe chipka deta hai. Ise na padho to call bina
         * kisi error ke gayab ho jaati hai — sirf 2-3 second baad screen wapas.
         */
        const { cause, causeCode, sipCode, sipReason } = call;
        const rejected = !answeredRef.current && !localHangupRef.current;

        if (rejected && (cause || sipCode)) {
          console.warn("[Telnyx] call rejected", { cause, causeCode, sipCode, sipReason });
          const sipPart = sipCode ? ` (SIP ${sipCode}${sipReason ? ` ${sipReason}` : ""})` : "";
          safeSet(setLastError)(`Call failed: ${cause || "rejected"}${sipPart}`);
        }

        const reason = localHangupRef.current
          ? "local"
          : answeredRef.current
          ? "remote"
          : "no-answer";
        closeCallLog(rejected && !!(cause || sipCode));
        resetCallState(reason);
      }
    },
    [safeSet, startTimer, resetCallState, closeCallLog, patchCallLog, clearRingTimer]
  );

  const initClient = useCallback(async () => {
    if (!isMountedRef.current) return;
    safeSet(setDeviceStatus)("initializing");
    safeSet(setLastError)(null);

    try {
      const auth =
        initialAuth?.token || (initialAuth?.authMode === "sip" && initialAuth?.login)
          ? initialAuth
          : await apiPost("/dialer/telnyx/token", { agentId });

      console.log("[Dialer] Telnyx login", {
        authMode: auth.authMode,
        sipUsername: auth.login || auth.connection?.sipUsername,
        callerId: auth.callerId || callerId,
      });

      // Mic permission probe
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
        probe.getTracks().forEach((t) => t.stop());
      } catch (micErr) {
        safeSet(setLastError)("Microphone access is blocked — allow it to make calls");
        safeSet(setDeviceStatus)("error");
        return;
      }

      if (clientRef.current) {
        try {
          await clientRef.current.disconnect();
        } catch (_) {}
        clientRef.current = null;
      }

      // trickleIce ke bina SDK poore ICE gathering ka intezaar karta hai offer
      // bhejne se pehle — atak jaye to call bina kisi error ke mar jaati hai
      // (signalingState "have-local-offer" pe ruki rehti hai).
      // Server decides how we authenticate: SIP login (the connection that
      // actually carries calls) or a telephony-credential token.
      const client = new TelnyxRTC({
        ...(auth.authMode === "sip"
          ? { login: auth.login, password: auth.password }
          : { login_token: auth.token }),
        env: TELNYX_RTC_ENV,
        prefetchIceCandidates: true,
        trickleIce: true,
      });
      client.remoteElement = ensureRemoteAudio();
      client.enableMicrophone();

      client.on("telnyx.ready", () => safeSet(setDeviceStatus)("ready"));
      client.on("telnyx.socket.close", () => safeSet(setDeviceStatus)("idle"));
      client.on("telnyx.error", (err) => {
        safeSet(setLastError)(err?.error?.message || err?.message || "Telnyx connection error");
        safeSet(setDeviceStatus)("error");
      });
      client.on("telnyx.notification", (notification) => {
        if (notification?.type === "callUpdate") handleCallUpdate(notification.call);
      });

      await client.connect();
      clientRef.current = client;
    } catch (err) {
      safeSet(setLastError)(
        err?.status === 401
          ? "Session expired — please log in again"
          : err?.message ?? "Failed to start the dialer"
      );
      safeSet(setDeviceStatus)("error");
    }
  }, [agentId, callerId, initialAuth, handleCallUpdate, safeSet]);

  useEffect(() => {
    isMountedRef.current = true;
    if (enabled) initClient();
    return () => {
      isMountedRef.current = false;
      clearInterval(timerRef.current);
      clearRingTimer();
      releaseHold(activeCallRef.current);
      try {
        clientRef.current?.disconnect();
      } catch (_) {}
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const makeCall = useCallback(
    async (rawNumber) => {
      if (!clientRef.current || !rawNumber?.trim()) return;
      if (deviceStatus !== "ready") {
        safeSet(setLastError)("Dialer not ready — please wait");
        return;
      }
      if (!callerId) {
        safeSet(setLastError)("No caller ID assigned to this agent");
        return;
      }

      const to = rawNumber.trim().startsWith("+")
        ? rawNumber.trim()
        : `+1${rawNumber.replace(/\D/g, "")}`;

      safeSet(setCallState)("calling");
      safeSet(setLastError)(null);
      safeSet(setCallEndReason)(null);
      localHangupRef.current = false;
      answeredRef.current = false;
      durationRef.current = 0;
      stampedIdRef.current = false;

      // 30-second ring timeout — agar samne wala 30s tak na uthaye to call khud hang up ho jaye.
      clearRingTimer();
      ringTimerRef.current = setTimeout(() => {
        if (!answeredRef.current && activeCallRef.current) {
          console.warn("[Dialer] Ring timeout reached (30s) — auto hanging up to prevent redial loop");
          localHangupRef.current = true;
          try {
            activeCallRef.current.hangup();
          } catch (_) {}
          closeCallLog(false);
          resetCallState("no-answer");
        }
      }, RING_TIMEOUT_MS);

      try {
        console.log("[Dialer] placing call", { to, callerNumber: callerId });
        const call = clientRef.current.newCall({
          destinationNumber: to,
          callerNumber: callerId,
          callerName: callerName || "",
          remoteElement: ensureRemoteAudio(),
          audio: true,
          video: false,
        });
        activeCallRef.current = call;

        // Log it now so a dropped socket still leaves a record behind.
        apiPost("/dialer/calls", {
          to,
          from: callerId,
          provider: "telnyx",
          callerName,
          callControlId: call?.telnyxIDs?.telnyxCallControlId || null,
        })
          .then((data) => {
            logIdRef.current = data?.id || null;
          })
          .catch(() => {});
      } catch (err) {
        clearRingTimer();
        safeSet(setLastError)(err?.message ?? "Failed to connect call");
        safeSet(setCallState)("idle");
      }
    },
    [deviceStatus, callerId, callerName, safeSet, clearRingTimer, closeCallLog, resetCallState]
  );

  const hangUp = useCallback(() => {
    clearRingTimer();
    localHangupRef.current = true;
    const call = activeCallRef.current;
    if (call) {
      try {
        call.hangup();
      } catch (_) {}
    } else {
      closeCallLog(false);
      resetCallState("local");
    }
  }, [clearRingTimer, resetCallState, closeCallLog]);

  const acceptCall = useCallback(() => {
    try {
      activeCallRef.current?.answer({ remoteElement: ensureRemoteAudio() });
    } catch (_) {}
  }, []);

  const rejectCall = useCallback(() => {
    localHangupRef.current = true;
    try {
      activeCallRef.current?.hangup();
    } catch (_) {}
  }, []);

  const toggleMute = useCallback(() => {
    const call = activeCallRef.current;
    if (!call || held) return;
    setMuted((prev) => {
      const next = !prev;
      try {
        if (next) call.muteAudio();
        else call.unmuteAudio();
      } catch (_) {}
      return next;
    });
  }, [held]);

  /*
   * Side-effects jaan-boojh kar setHeld ke updater ke BAHAR hain. StrictMode
   * updater ko do baar chalata hai, aur tab do AudioContext + do interval ban
   * jaate — pehla wala kabhi band hi na hota aur beep bajti rehti.
   */
  const toggleHold = useCallback(() => {
    const call = activeCallRef.current;
    if (!call) return;
    const next = !held;

    if (next) {
      const sender = audioSender(call);
      const source = sender ? createHoldSource() : null;
      if (sender && source) {
        micTrackRef.current = sender.track;
        holdSourceRef.current = source;
        sender.replaceTrack(source.track).catch(() => {});
        // Hold matlab agent hat gaya — usse customer ki awaaz nahi aani chahiye.
        ensureRemoteAudio().muted = true;
      } else {
        // WebAudio ya sender na mile to kam se kam purana SIP hold lag jaye.
        try {
          call.hold();
        } catch (_) {}
      }
    } else if (!releaseHold(call)) {
      try {
        call.unhold();
      } catch (_) {}
    }

    safeSet(setHeld)(next);
    safeSet(setCallState)(next ? "held" : "active");
  }, [held, safeSet, releaseHold]);

  const toggleSpeaker = useCallback(() => {
    setSpeakerOn((prev) => {
      const next = !prev;
      document.querySelectorAll("audio").forEach(async (el) => {
        try {
          if (typeof el.setSinkId === "function") await el.setSinkId(next ? "speaker" : "default");
        } catch (_) {}
      });
      return next;
    });
  }, []);

  const sendDtmf = useCallback((digit) => {
    try {
      activeCallRef.current?.dtmf(digit);
    } catch (_) {}
  }, []);

  const retryInit = useCallback(() => initClient(), [initClient]);

  return {
    deviceStatus,
    callState,
    incomingFrom,
    callDuration,
    muted,
    held,
    speakerOn,
    lastError,
    callEndReason,
    makeCall,
    hangUp,
    acceptCall,
    rejectCall,
    toggleMute,
    toggleHold,
    toggleSpeaker,
    sendDtmf,
    retryInit,
  };
}

export default useTelnyxDialer;
